import * as Sentry from '@sentry/react';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ludolio/shared-ui';
import { Button } from '@ludolio/shared-ui';
import { Badge } from '@ludolio/shared-ui';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@ludolio/shared-ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ludolio/shared-ui';
import { Label } from '@ludolio/shared-ui';
import { Plus, Users, Trophy, Calendar } from 'lucide-react';
import { useGameStore } from '@/stores/gameStore';
import { useStudentStore } from '@/stores/studentStore';
import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/services/api';
import { getRegionAwareAgeRating, formatAgeRating as formatRating, getAgeRatingDescriptors as getDescriptors } from '@ludolio/shared-ui/utils/age-rating';
import type { Game } from '@/types';

// Per-game stats computed from aggregate teacher analytics
interface GameStats {
  achievements: number;
  uniqueStudentCount: number;
  unitCount: number;
}

function AgeRatingBadge({ game, orgRegion }: { game: Game; orgRegion?: string | null }) {
  const rating = formatRating(getRegionAwareAgeRating(game.ageRatings, orgRegion));
  if (!rating) return null;

  return <Badge variant="outline" className="shrink-0 border-amber-300/60 bg-amber-50 text-amber-800">{rating}</Badge>;
}

export function GamesView() {
  const { games, fetchGames, isLoading: gamesLoading } = useGameStore();
  const { students, fetchStudents, isLoading: studentsLoading } = useStudentStore();
  const orgId = useAuthStore((state) => state.user?.orgId);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedGameId, setSelectedGameId] = useState<string>('');
  const [selectedStudentId, setSelectedStudentId] = useState<string>('');
  const [gameStats, setGameStats] = useState<Record<string, GameStats>>({});
  const [statsLoading, setStatsLoading] = useState(true);
  const [orgRegion, setOrgRegion] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    apiClient.getOrganization(orgId)
      .then((res) => {
        if (!cancelled && res.success) {
          setOrgRegion(res.data?.region ?? null);
        }
      })
      .catch(() => {
        // Age rating still works with default region fallback
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  // Fetch aggregate game analytics once instead of fan-out per game and per unit.
  useEffect(() => {
    const loadGameStats = async () => {
      setStatsLoading(true);

      try {
        const [overviewResponse] = await Promise.all([
          apiClient.getTeacherGamesOverview(),
          fetchGames(),
          fetchStudents(),
        ]);

        if (overviewResponse.success && overviewResponse.data) {
          const statsMap: Record<string, GameStats> = {};
          for (const stat of overviewResponse.data) {
            statsMap[stat.gameId] = {
              achievements: stat.achievements,
              uniqueStudentCount: stat.uniqueStudentCount,
              unitCount: stat.unitCount,
            };
          }
          setGameStats(statsMap);
        } else {
          throw new Error(overviewResponse.error || 'Failed to load game overview');
        }
      } catch (error) {
        console.error('Failed to load game stats:', error);
        Sentry.captureException(error);
      } finally {
        setStatsLoading(false);
      }
    };

    loadGameStats();
  }, [fetchGames, fetchStudents]);

  const loading = gamesLoading || studentsLoading || statsLoading;

  const handleAssignGame = async () => {
    if (!selectedGameId || !selectedStudentId) return;

    try {
      // Create assignment with default dates (now to 30 days from now)
      const startDate = new Date().toISOString();
      const endDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

      await apiClient.createAssignment({
        studentId: selectedStudentId,
        gameId: selectedGameId,
        startDate,
        endDate,
        isActive: true,
      });

      setAssignDialogOpen(false);
      setSelectedGameId('');
      setSelectedStudentId('');
      // Optionally refresh data or show success message
    } catch (error) {
      console.error('Failed to assign game:', error);
      Sentry.captureException(error);
      // Optionally show error message to user
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Games</h1> 
        </div>
        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogTrigger asChild>
            <Button className="w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-2" />
              Assign Game
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign Game to Student</DialogTitle>
              <DialogDescription>
                Select a game and student to create a new assignment
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="game">Game</Label>
                <Select value={selectedGameId} onValueChange={setSelectedGameId}>
                  <SelectTrigger id="game">
                    <SelectValue placeholder="Select a game" />
                  </SelectTrigger>
                  <SelectContent>
                    {games.map((game) => (
                      <SelectItem key={game.id} value={game.id}>
                        {game.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="student">Student</Label>
                <Select value={selectedStudentId} onValueChange={setSelectedStudentId}>
                  <SelectTrigger id="student">
                    <SelectValue placeholder="Select a student" />
                  </SelectTrigger>
                  <SelectContent>
                    {students.map((student) => (
                      <SelectItem key={student.id} value={student.id}>
                        {student.firstName} {student.lastName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAssignGame} disabled={!selectedGameId || !selectedStudentId}>
                  Assign Game
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading games...</div>
      ) : (
        <div
          className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
          role={games.length ? 'list' : undefined}
          aria-label={games.length ? 'Games' : undefined}
        >
          {games.length === 0 ? (
            <Card className="col-span-full">
              <CardContent className="py-8 text-center text-muted-foreground">
                No games available
              </CardContent>
            </Card>
          ) : (
            games.map((game) => {
              const stats = gameStats[game.id];
              const totalAchievements = stats?.achievements ?? 0;
              const uniqueStudents = stats?.uniqueStudentCount ?? 0;
              const unitCount = stats?.unitCount ?? 0;

            return (
              <Card key={game.id} className="overflow-hidden" role="listitem">
                <div className="aspect-video bg-gradient-to-br from-primary/20 to-primary/5 relative overflow-hidden">
                  {game.bannerUrl ? (
                    <img
                      src={game.bannerUrl}
                      alt={game.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Trophy className="w-16 h-16 text-primary/30" />
                    </div>
                  )}
                  <Badge className="absolute top-2 right-2">
                    v{game.version}
                  </Badge>
                </div>
	                <CardHeader>
	                  <CardTitle>{game.title}</CardTitle>
                  <CardDescription className="line-clamp-2 min-h-[2.5rem]">
                    {game.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Users className="w-4 h-4" />
                        <span>Students</span>
                      </div>
                      <span className="font-semibold">{uniqueStudents}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Trophy className="w-4 h-4" />
                        <span>Achievements</span>
                      </div>
                      <span className="font-semibold">{totalAchievements}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        <span>Assignments</span>
                      </div>
                      <span className="font-semibold">{unitCount}</span>
                    </div>
                    <div className="pt-2 border-t space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs text-muted-foreground">
                          Publisher: {game.publisher}
                        </p>
                        <AgeRatingBadge game={game} orgRegion={orgRegion} />
                      </div>
	                      {getDescriptors(getRegionAwareAgeRating(game.ageRatings, orgRegion)).length > 0 && (
	                        <p className="text-xs text-muted-foreground">
	                          Rating notes: {getDescriptors(getRegionAwareAgeRating(game.ageRatings, orgRegion)).join(', ')}
	                        </p>
	                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

